import React from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import DashboardHome from './pages/DashboardHome';
import CommunityPostPage from './pages/CommunityPostPage';
import Layout from './components/Layout';
import AnnouncementsPage from './pages/AnnouncementsPage';
import CreateAnnouncementPage from './pages/CreateAnnouncementPage';
import EventsPage from './pages/EventsPage';
import PostsPage from './pages/PostsPage';
import PrayersPage from './pages/PrayersPage';
import UsersPage from './pages/UsersPage';
import WelcomeInOptInPage from './pages/WelcomeInOptInPage';
import WelcomeInPartnerConversionsPage from './pages/WelcomeInPartnerConversionsPage';
import SermonsPage from './pages/SermonsPage';
import SermonSeriesPage from './pages/SermonSeriesPage';
import SermonSeriesFormPage from './pages/SermonSeriesFormPage';
import SermonFormPage from './pages/SermonFormPage';
import DeclarationsPage from './pages/DeclarationsPage';
import DevotionalsPage from './pages/DevotionalsPage';
import VolunteerPage from './pages/VolunteerPage';
import VolunteerFormPage from './pages/VolunteerFormPage';
import VolunteerCategoriesPage from './pages/VolunteerCategoriesPage';
import VolunteerRequestsPage from './pages/VolunteerRequestsPage';
import MaintenanceReportsPage from './pages/MaintenanceReportsPage';
import PartnerRequestsPage from './pages/PartnerRequestsPage';
import CommunityGroupsPage from './pages/CommunityGroupsPage';
import CommunityGroupDetailPage from './pages/CommunityGroupDetailPage';
import ScheduledPostsPage from './pages/ScheduledPostsPage';
import CommunityGroupPostPage from './pages/CommunityGroupPostPage';
import CommunityGroupChatThreadPage from './pages/CommunityGroupChatThreadPage';
import GlobalSearchPage from './pages/GlobalSearchPage';
import GroupsPage from './pages/GroupsPage';
import GroupManagePage from './pages/GroupManagePage';
import BusinessesPage from './pages/BusinessesPage';
import SubmittedBusinessesPage from './pages/SubmittedBusinessesPage';
import GroupGoalsPage from './pages/GroupGoalsPage';
import KidsChurchPage from './pages/KidsChurchPage';
import KidsChurchChildFormPage from './pages/KidsChurchChildFormPage';
import RequireAuth from './components/RequireAuth';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import NewPartnerSignUpPage from './pages/NewPartnerSignUpPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DeclarationFormPage from './pages/DeclarationFormPage';
import DevotionalSeriesFormPage from './pages/DevotionalSeriesFormPage';
import DevotionalFormPage from './pages/DevotionalFormPage';
import DevotionalSeriesPage from './pages/DevotionalSeriesPage';
import KidsChurchMessagesPage from './pages/KidsChurchMessagesPage';
import UsersMessagesPage from './pages/UsersMessagesPage';
import EventSubmissionPage from './pages/EventSubmissionPage';
import PublicEventRegistrationsPage, {
  PublicEventRegistrationsEmailPage,
  PublicEventRegistrationsEventPage,
  PublicEventRegistrationsSmsPage,
} from './pages/PublicEventRegistrationsPage';
import EventSetupPage from './pages/EventSetupPage';
import { TriviaPage } from './pages/TriviaPage';
import { TriviaResultsPage } from './pages/TriviaResultsPage';
import { useAuth } from './context/AuthContext';
import { useCurrentUserProfile } from './hooks/useCurrentUserProfile';
import { useMyEventAdminEventIds } from './hooks/useEventAdmins';
import { Permission, hasAnyPermission } from './utils/roles';
import SuperAdminPage from './pages/SuperAdminPage';
import AppIssuesPage from './pages/AppIssuesPage';
import MemberDetailPage from './pages/MemberDetailPage';
import ProfilePage from './pages/ProfilePage';
import HouseholdsPage from './pages/HouseholdsPage';
import GrowthOverviewPage from './pages/growth/GrowthOverviewPage';
import GrowthAuditsPage from './pages/growth/GrowthAuditsPage';
import GrowthPeersPage from './pages/growth/GrowthPeersPage';
import GrowthBriefsPage from './pages/growth/GrowthBriefsPage';
import GrowthTasksPage from './pages/growth/GrowthTasksPage';
import GrowthSettingsPage from './pages/growth/GrowthSettingsPage';
import WebsitePage from './pages/WebsitePage';
import WebsiteStoreManagementPage from './pages/WebsiteStoreManagementPage';
import { ContactRequestsPage } from './pages/ContactRequestsPage';
import { NcofEducatorsAdminPage } from './pages/NcofEducatorsAdminPage';
import { StudentsAdminPage } from './pages/StudentsAdminPage';
import { AssessmentsAdminPage } from './pages/AssessmentsAdminPage';
import QuestionsPage from './pages/QuestionsPage';
import MarketingDashboardPage from './pages/MarketingDashboardPage';
import MarketingCampaignsPage from './pages/MarketingCampaignsPage';
import NewMessagePage from './pages/NewMessagePage';
import SMSFlowPage from './pages/SMSFlowPage';
import KeywordManagementPage from './pages/KeywordManagementPage';
import FunnelAnalyticsPage from './pages/FunnelAnalyticsPage';
import SubscribersPage from './pages/SubscribersPage';
import ReportsPage from './pages/ReportsPage';
import MarketingCampaignDetailsPage from './pages/MarketingCampaignDetailsPage';
import MarketingInboxPage from './pages/MarketingInboxPage';
import SocialDashboardPage from './pages/social/SocialDashboardPage';
import SocialAccountsPage from './pages/social/SocialAccountsPage';
import SocialComposerPage from './pages/social/SocialComposerPage';
import SocialCalendarPage from './pages/social/SocialCalendarPage';
import SocialApprovalsPage from './pages/social/SocialApprovalsPage';
import SocialAnalyticsPage from './pages/social/SocialAnalyticsPage';
import SocialMediaLibraryPage from './pages/social/SocialMediaLibraryPage';
import ClassroomTeacherLayout from './pages/classroom/ClassroomTeacherLayout';
import ClassroomTeacherOverviewPage from './pages/classroom/ClassroomTeacherOverviewPage';
import ClassroomTeacherLessonsPage from './pages/classroom/ClassroomTeacherLessonsPage';
import ClassroomTeacherQuizzesPage from './pages/classroom/ClassroomTeacherQuizzesPage';
import ClassroomTeacherQuizEditorPage from './pages/classroom/ClassroomTeacherQuizEditorPage';
import ClassroomTeacherAssignmentsPage from './pages/classroom/ClassroomTeacherAssignmentsPage';
import ClassroomTeacherAssignmentEditorPage from './pages/classroom/ClassroomTeacherAssignmentEditorPage';
import ClassroomTeacherAssignmentDetailPage from './pages/classroom/ClassroomTeacherAssignmentDetailPage';
import ClassroomTeacherGradesPage from './pages/classroom/ClassroomTeacherGradesPage';
import ClassroomTeacherSubmissionGradingPage from './pages/classroom/ClassroomTeacherSubmissionGradingPage';
import ClassroomTeacherAnalyticsPage from './pages/classroom/ClassroomTeacherAnalyticsPage';
import ClassroomTeacherCommunicationPage from './pages/classroom/ClassroomTeacherCommunicationPage';
import ClassroomTeacherNewClassPage from './pages/classroom/ClassroomTeacherNewClassPage';
import ClassroomTeacherClassesPage from './pages/classroom/ClassroomTeacherClassesPage';
import ClassroomTeacherClassDetailPage from './pages/classroom/ClassroomTeacherClassDetailPage';
import ClassroomTeacherClassEditPage from './pages/classroom/ClassroomTeacherClassEditPage';
import ClassroomTeacherClassLessonsPage from './pages/classroom/ClassroomTeacherClassLessonsPage';
import ClassroomTeacherClassAssignmentsPage from './pages/classroom/ClassroomTeacherClassAssignmentsPage';
import ClassroomTeacherClassStudentsPage from './pages/classroom/ClassroomTeacherClassStudentsPage';
import ClassroomTeacherClassSyllabusPage from './pages/classroom/ClassroomTeacherClassSyllabusPage';
import ClassroomTeacherClassTextbookPage from './pages/classroom/ClassroomTeacherClassTextbookPage';
import ClassroomTeacherClassLessonEditorPage from './pages/classroom/ClassroomTeacherClassLessonEditorPage';
import ClassroomTeacherClassDiscussionPage from './pages/classroom/ClassroomTeacherClassDiscussionPage';
import FinancePage from './pages/FinancePage';
import FinanceDonorPage from './pages/FinanceDonorPage';
import ContentApprovalAdminPage from './pages/ContentApprovalAdminPage';
import ChurchPlannerLayout from './pages/church-planner/ChurchPlannerLayout';
import ChurchPlannerHomePage from './pages/church-planner/ChurchPlannerHomePage';
import ChurchPlannerAssignmentsPage from './pages/church-planner/ChurchPlannerAssignmentsPage';
import ChurchPlannerMyAssignmentsPage from './pages/church-planner/ChurchPlannerMyAssignmentsPage';
import ChurchPlannerCalendarPage from './pages/church-planner/ChurchPlannerCalendarPage';
import ChurchPlannerSchedulePage from './pages/church-planner/ChurchPlannerSchedulePage';
import ChurchPlannerServiceDetailsPage from './pages/church-planner/ChurchPlannerServiceDetailsPage';
import ChurchPlannerPeoplePage from './pages/church-planner/ChurchPlannerPeoplePage';
import ChurchPlannerPersonDetailPage from './pages/church-planner/ChurchPlannerPersonDetailPage';
import ChurchPlannerProfilePage from './pages/church-planner/ChurchPlannerProfilePage';
import ChurchPlannerServiceHealthPage from './pages/church-planner/ChurchPlannerServiceHealthPage';
import ChurchPlannerTeamHealthDetailPage from './pages/church-planner/ChurchPlannerTeamHealthDetailPage';
import ChurchPlannerActivityPage from './pages/church-planner/ChurchPlannerActivityPage';
import ChurchPlannerResourcesPage from './pages/church-planner/ChurchPlannerResourcesPage';
import ChurchPlannerSongsPage from './pages/church-planner/ChurchPlannerSongsPage';
import ChurchPlannerCreateServicePage from './pages/church-planner/ChurchPlannerCreateServicePage';
import ChurchPlannerSongCreatePage from './pages/church-planner/ChurchPlannerSongCreatePage';
import ChurchPlannerSongDetailsPage from './pages/church-planner/ChurchPlannerSongDetailsPage';
import ChurchPlannerSettingsPage from './pages/church-planner/ChurchPlannerSettingsPage';
import ChurchPlannerSupportPage from './pages/church-planner/ChurchPlannerSupportPage';
import FacilityAdminPage from './pages/FacilityAdminPage';
import NotificationsPage from './pages/NotificationsPage';

function ScrollToTopOnNavigation() {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  return null;
}

function ProtectedRoutes() {
  return (
    <RequireAuth>
      <Layout>
        <Outlet />
      </Layout>
    </RequireAuth>
  );
}

function PermissionRoutes({
  permissions,
  redirectTo = '/events/submission',
}: {
  permissions: Permission[];
  redirectTo?: string;
}) {
  const { user, loading } = useAuth();
  const { data: profile, isLoading } = useCurrentUserProfile(user?.id);

  if (loading || isLoading) {
    return <div>Loading...</div>;
  }

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const hasAccess = hasAnyPermission(roles, permissions);

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}

function EventRegistrantAdminRoutes({
  redirectTo = '/profile',
}: {
  redirectTo?: string;
}) {
  const { user, loading } = useAuth();
  const { data: profile, isLoading } = useCurrentUserProfile(user?.id);
  const { data: assignedEventIds = [], isLoading: isLoadingEventAdmins } = useMyEventAdminEventIds(user?.id);

  if (loading || isLoading || isLoadingEventAdmins) {
    return <div>Loading...</div>;
  }

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const hasAccess = hasAnyPermission(roles, [Permission.MANAGE_EVENTS]) || assignedEventIds.length > 0;

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}

function LegacyCampaignsRedirect() {
  const { campaignId } = useParams<{ campaignId?: string }>();
  const destination = campaignId
    ? `/marketing/messages/${encodeURIComponent(campaignId)}`
    : '/marketing/messages';
  return <Navigate to={destination} replace />;
}

function LegacySmsFlowRedirect() {
  return <Navigate to="/marketing/keywords" replace />;
}

function App() {
  return (
    <>
      <ScrollToTopOnNavigation />
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/new-partner" element={<NewPartnerSignUpPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected dashboard routes */}
        <Route element={<ProtectedRoutes />}>
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route
            element={
              <PermissionRoutes
                permissions={[Permission.MANAGE_EVENTS, Permission.SUBMIT_EVENT_REQUESTS]}
                redirectTo="/profile"
              />
            }
          >
            <Route path="/events/submission" element={<EventSubmissionPage />} />
          </Route>

          <Route
            element={
              <PermissionRoutes permissions={[Permission.VIEW_DASHBOARD]} redirectTo="/profile" />
            }
          >
            <Route path="/" element={<DashboardHome />} />
            <Route path="/search" element={<GlobalSearchPage />} />
            <Route path="/scheduled-posts" element={<ScheduledPostsPage />} />
            <Route path="/community/posts/:postId" element={<CommunityPostPage />} />
            <Route path="/groups" element={<CommunityGroupsPage />} />
            <Route path="/groups/:groupId" element={<CommunityGroupDetailPage />} />
            <Route path="/groups/:groupId/posts/:postId" element={<CommunityGroupPostPage />} />
            <Route path="/groups/:groupId/chats/:chatId" element={<CommunityGroupChatThreadPage />} />
          </Route>

          <Route
            element={
              <PermissionRoutes permissions={[Permission.VIEW_MARKETING]} redirectTo="/profile" />
            }
          >
            <Route path="/marketing" element={<MarketingDashboardPage />} />
            <Route path="/marketing/messages" element={<MarketingCampaignsPage />} />
            <Route path="/marketing/campaigns" element={<LegacyCampaignsRedirect />} />
            <Route path="/marketing/new-message" element={<NewMessagePage />} />
            <Route path="/marketing/sms-flow" element={<LegacySmsFlowRedirect />} />
            <Route path="/marketing/sms-flow/:flowId" element={<SMSFlowPage />} />
            <Route path="/marketing/keywords" element={<KeywordManagementPage />} />
            <Route path="/marketing/funnel-analytics" element={<FunnelAnalyticsPage />} />
            <Route path="/marketing/subscribers" element={<SubscribersPage />} />
            <Route path="/marketing/reports" element={<ReportsPage />} />
            <Route path="/marketing/inbox" element={<MarketingInboxPage />} />
            <Route path="/marketing/messages/:campaignId" element={<MarketingCampaignDetailsPage />} />
            <Route path="/marketing/campaigns/:campaignId" element={<LegacyCampaignsRedirect />} />
            <Route path="/marketing/social" element={<SocialDashboardPage />} />
            <Route path="/marketing/social/accounts" element={<SocialAccountsPage />} />
            <Route path="/marketing/social/composer" element={<SocialComposerPage />} />
            <Route path="/marketing/social/composer/:postId" element={<SocialComposerPage />} />
            <Route path="/marketing/social/calendar" element={<SocialCalendarPage />} />
            <Route path="/marketing/social/approvals" element={<SocialApprovalsPage />} />
            <Route path="/marketing/social/analytics" element={<SocialAnalyticsPage />} />
            <Route path="/marketing/social/library" element={<SocialMediaLibraryPage />} />
          </Route>

          <Route
            element={
              <PermissionRoutes
                permissions={[Permission.MANAGE_CONTENT, Permission.VIEW_ANNOUNCEMENTS]}
              />
            }
          >
            <Route path="/announcements" element={<AnnouncementsPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MANAGE_CONTENT]} />}>
            <Route path="/announcements/new" element={<CreateAnnouncementPage />} />
            <Route path="/announcements/create" element={<CreateAnnouncementPage />} />
            <Route path="/announcements/:announcementId/edit" element={<CreateAnnouncementPage />} />
            <Route path="/trivia" element={<TriviaPage />} />
            <Route path="/trivia/results" element={<TriviaResultsPage />} />
            <Route path="/sermon-series/new" element={<SermonSeriesFormPage />} />
            <Route path="/sermon-series/:id" element={<SermonSeriesFormPage />} />
            <Route path="/sermons/new" element={<SermonFormPage />} />
            <Route path="/sermons/:id" element={<SermonFormPage />} />
            <Route path="/declarations" element={<DeclarationsPage />} />
            <Route path="/declarations/new" element={<DeclarationFormPage />} />
            <Route path="/declarations/:id" element={<DeclarationFormPage />} />
            <Route path="/devotionals" element={<DevotionalsPage />} />
            <Route path="/devotionals/new" element={<DevotionalFormPage />} />
            <Route path="/devotionals/:id" element={<DevotionalFormPage />} />
            <Route path="/devotional-series" element={<DevotionalSeriesPage />} />
            <Route path="/devotional-series/new" element={<DevotionalSeriesFormPage />} />
            <Route path="/devotional-series/:id" element={<DevotionalSeriesFormPage />} />
          </Route>

          <Route
            element={
              <PermissionRoutes
                permissions={[Permission.MANAGE_CONTENT, Permission.VIEW_SERMON_CONTENT]}
                redirectTo="/"
              />
            }
          >
            <Route path="/sermons" element={<SermonsPage />} />
            <Route path="/sermon-series" element={<SermonSeriesPage />} />
          </Route>

          <Route
            element={
              <PermissionRoutes
                permissions={[Permission.MANAGE_SERMON_QUESTIONS, Permission.VIEW_SERMON_QUESTIONS]}
                redirectTo="/"
              />
            }
          >
            <Route path="/questions" element={<QuestionsPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MANAGE_CONTENT]} redirectTo="/profile" />}>
            <Route path="/admin/website" element={<WebsitePage />} />
            <Route path="/admin/website/store" element={<WebsiteStoreManagementPage />} />
            <Route path="/admin/content-approval" element={<ContentApprovalAdminPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.VIEW_USERS]} redirectTo="/profile" />}>
            <Route path="/admin/contact-requests" element={<ContactRequestsPage />} />
            <Route path="/admin/ncof-educators" element={<NcofEducatorsAdminPage />} />
            <Route path="/admin/students" element={<StudentsAdminPage />} />
            <Route path="/admin/assessments" element={<AssessmentsAdminPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MANAGE_EVENTS]} />}>
            <Route path="/events/setup/:eventId" element={<EventSetupPage />} />
          </Route>

          <Route element={<EventRegistrantAdminRoutes />}>
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/public-registrations" element={<PublicEventRegistrationsPage />} />
            <Route path="/events/public-registrations/:eventId" element={<PublicEventRegistrationsEventPage />} />
            <Route path="/events/public-registrations/:eventId/email" element={<PublicEventRegistrationsEmailPage />} />
            <Route path="/events/public-registrations/:eventId/sms" element={<PublicEventRegistrationsSmsPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MANAGE_FACILITIES]} />}>
            <Route path="/facilities" element={<FacilityAdminPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.VIEW_CHURCH_PLANNER]} />}>
            <Route path="/church-planner" element={<ChurchPlannerLayout />}>
              <Route index element={<Navigate to="/church-planner/dashboard" replace />} />
              <Route path="dashboard" element={<ChurchPlannerHomePage />} />
              <Route path="my-assignments" element={<ChurchPlannerMyAssignmentsPage />} />
              <Route path="calendar" element={<ChurchPlannerCalendarPage />} />
              <Route path="assignments" element={<ChurchPlannerAssignmentsPage />} />
              <Route path="schedule" element={<ChurchPlannerSchedulePage />} />
              <Route
                element={
                  <PermissionRoutes
                    permissions={[Permission.CREATE_CHURCH_PLAN]}
                    redirectTo="/church-planner/assignments"
                  />
                }
              >
                <Route path="services/new" element={<ChurchPlannerCreateServicePage />} />
              </Route>
              <Route path="services/:serviceId" element={<ChurchPlannerServiceDetailsPage />} />
              <Route path="services/:serviceId/health/:teamId" element={<ChurchPlannerTeamHealthDetailPage />} />
              <Route path="service-health" element={<ChurchPlannerServiceHealthPage />} />
              <Route path="activity" element={<ChurchPlannerActivityPage />} />
              <Route
                element={
                  <PermissionRoutes
                    permissions={[Permission.MANAGE_CHURCH_PLANNER_ROLES]}
                    redirectTo="/church-planner/dashboard"
                  />
                }
              >
                <Route path="people" element={<ChurchPlannerPeoplePage />} />
                <Route path="people/:personId" element={<ChurchPlannerPersonDetailPage />} />
              </Route>
              <Route path="songs" element={<ChurchPlannerSongsPage />} />
              <Route path="songs/new" element={<ChurchPlannerSongCreatePage />} />
              <Route path="songs/:songId" element={<ChurchPlannerSongDetailsPage />} />
              <Route path="profile" element={<ChurchPlannerProfilePage />} />
              <Route path="resources" element={<ChurchPlannerResourcesPage />} />
              <Route
                element={
                  <PermissionRoutes
                    permissions={[
                      Permission.CREATE_CHURCH_PLAN,
                      Permission.MANAGE_CHURCH_PLANNER_ROLES,
                      Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
                    ]}
                    redirectTo="/church-planner/assignments"
                  />
                }
              >
                <Route path="settings" element={<ChurchPlannerSettingsPage />} />
              </Route>
              <Route path="support" element={<ChurchPlannerSupportPage />} />
            </Route>
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MODERATE_COMMUNITY]} />}>
            <Route path="/posts" element={<PostsPage />} />
            <Route path="/prayers" element={<PrayersPage />} />
            <Route path="/groups/admin" element={<GroupsPage />} />
            <Route path="/businesses" element={<BusinessesPage />} />
            <Route path="/businesses/submitted" element={<SubmittedBusinessesPage />} />
          </Route>

          <Route path="/groups/:groupId/manage" element={<GroupManagePage />} />

          <Route element={<PermissionRoutes permissions={[Permission.VIEW_GROUP_GOALS]} redirectTo="/" />}>
            <Route path="/group-goals" element={<GroupGoalsPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MANAGE_VOLUNTEERS]} />}>
            <Route path="/volunteers" element={<VolunteerPage />} />
            <Route path="/volunteers/new" element={<VolunteerFormPage />} />
            <Route path="/volunteers/:id" element={<VolunteerFormPage />} />
            <Route path="/volunteers/categories" element={<VolunteerCategoriesPage />} />
            <Route path="/volunteers/requests" element={<VolunteerRequestsPage />} />
            <Route path="/volunteers/maintenance-reports" element={<MaintenanceReportsPage />} />
          </Route>

          <Route
            element={
              <PermissionRoutes
                permissions={[Permission.MANAGE_VOLUNTEERS, Permission.VIEW_PARTNER_REQUESTS]}
              />
            }
          >
            <Route path="/partners/requests" element={<PartnerRequestsPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.MANAGE_KIDS_CHURCH]} />}>
            <Route path="/kids-church" element={<KidsChurchPage />} />
            <Route path="/kids-church/new" element={<KidsChurchChildFormPage />} />
            <Route path="/kids-church/messages" element={<KidsChurchMessagesPage />} />
            <Route path="/classroom-teacher" element={<ClassroomTeacherLayout />}>
              <Route index element={<ClassroomTeacherOverviewPage />} />
              <Route path="classes" element={<ClassroomTeacherClassesPage />} />
              <Route path="classes/:classId" element={<ClassroomTeacherClassDetailPage />} />
              <Route path="classes/:classId/edit" element={<ClassroomTeacherClassEditPage />} />
              <Route path="classes/:classId/lessons" element={<ClassroomTeacherClassLessonsPage />} />
              <Route path="classes/:classId/assignments" element={<ClassroomTeacherClassAssignmentsPage />} />
              <Route path="classes/:classId/discussion" element={<ClassroomTeacherClassDiscussionPage />} />
              <Route path="classes/:classId/students" element={<ClassroomTeacherClassStudentsPage />} />
              <Route path="classes/:classId/syllabus" element={<ClassroomTeacherClassSyllabusPage />} />
              <Route path="classes/:classId/textbook" element={<ClassroomTeacherClassTextbookPage />} />
              <Route path="classes/:classId/grades" element={<ClassroomTeacherGradesPage />} />
              <Route path="classes/:classId/lessons/new" element={<ClassroomTeacherClassLessonEditorPage />} />
              <Route path="classes/:classId/lessons/:lessonId/edit" element={<ClassroomTeacherClassLessonEditorPage />} />
              <Route path="classes/new" element={<ClassroomTeacherNewClassPage />} />
              <Route path="lessons" element={<ClassroomTeacherLessonsPage />} />
              <Route path="quizzes" element={<ClassroomTeacherQuizzesPage />} />
              <Route path="quizzes/new" element={<ClassroomTeacherQuizEditorPage />} />
              <Route path="quizzes/:quizId/edit" element={<ClassroomTeacherQuizEditorPage />} />
              <Route path="assignments/new" element={<ClassroomTeacherAssignmentEditorPage />} />
              <Route path="assignments/:assignmentId" element={<ClassroomTeacherAssignmentDetailPage />} />
              <Route path="assignments/:assignmentId/edit" element={<ClassroomTeacherAssignmentEditorPage />} />
              <Route path="assignments" element={<ClassroomTeacherAssignmentsPage />} />
              <Route path="grades" element={<ClassroomTeacherGradesPage />} />
              <Route path="grades/:submissionId" element={<ClassroomTeacherSubmissionGradingPage />} />
              <Route path="analytics" element={<ClassroomTeacherAnalyticsPage />} />
              <Route path="communication" element={<ClassroomTeacherCommunicationPage />} />
            </Route>
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.VIEW_USERS]} />}>
            <Route path="/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/users/households" element={<HouseholdsPage />} />
            <Route path="/admin/users/messages" element={<UsersMessagesPage />} />
            <Route path="/members/:id" element={<MemberDetailPage />} />
            <Route path="/admin/members/:id" element={<MemberDetailPage />} />
            <Route path="/users/welcome-in-opt-in" element={<Navigate to="/admin/users/welcome-in-opt-in" replace />} />
            <Route path="/admin/users/welcome-in-opt-in" element={<WelcomeInOptInPage />} />
            <Route path="/users/welcome-in-opt-in" element={<WelcomeInOptInPage />} />
            <Route
              path="/admin/users/welcome-in-partners"
              element={<WelcomeInPartnerConversionsPage />}
            />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.VIEW_GROWTH]} redirectTo="/" />}>
            <Route path="/growth" element={<GrowthOverviewPage />} />
            <Route path="/growth/audits" element={<GrowthAuditsPage />} />
            <Route path="/growth/peers" element={<GrowthPeersPage />} />
            <Route path="/growth/briefs" element={<GrowthBriefsPage />} />
            <Route path="/growth/tasks" element={<GrowthTasksPage />} />
            <Route path="/growth/settings" element={<GrowthSettingsPage />} />
          </Route>

          <Route element={<PermissionRoutes permissions={[Permission.VIEW_SUPER_ADMIN]} redirectTo="/" />}>
            <Route path="/super-admin" element={<SuperAdminPage />} />
            <Route path="/super-admin/app-issues" element={<AppIssuesPage />} />
          </Route>
          <Route element={<PermissionRoutes permissions={[Permission.VIEW_FINANCE]} redirectTo="/" />}>
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/finance/donor/:donorType/:donorValue" element={<FinanceDonorPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
